<template>
  <ModalException
    v-if="result === 'OauthSignUpResult.Exception'"
    @close="onModalCloseException"
    :openOnInit="result === 'OauthSignUpResult.Exception'"
  />
  <ModalCantRetrieveEmail
    v-if="result === 'OauthSignUpResult.CantRetrieveEmail'"
    @close="onModalCloseCantRetrieveEmail"
    :openOnInit="result === 'OauthSignUpResult.CantRetrieveEmail'"
  />
  <ModalUserAlreadyRegistered
    v-if="result === 'OauthSignUpResult.UserAlreadyRegistered'"
    @close="onModalCloseUserAlreadyRegistered"
    :email="UserAlreadyRegisteredEmail"
    :oauthProvider="OauthProvider"
    :openOnInit="result === 'OauthSignUpResult.UserAlreadyRegistered'"
  />
  <ModalUserAlreadyRegistered
    v-if="result === 'LocalSignUpResult.UserAlreadyRegistered'"
    @close="onModalCloseUserAlreadyRegistered"
    :email="UserAlreadyRegisteredEmail"
    :openOnInit="result === 'LocalSignUpResult.UserAlreadyRegistered'"
  />
  <div class="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <img class="mx-auto h-[100px] w-auto" src="/img/spike-logo.svg" alt="Spike" />
      <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900">Sign up for a free account</h2>
    </div>

    <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
      <div
        class="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10"
        :class="result === 'LocalSignUpResult.Fail' ? 'shakeX' : ''"
      >
        <div class="space-y-6">
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700"> Email address </label>
            <div class="mt-1">
              <input
                v-model="email"
                name="email"
                type="email"
                autocomplete="email"
                required
                class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary-600 sm:text-sm"
              />
            </div>
          </div>

          <div>
            <label for="password" class="block text-sm font-medium text-gray-700">Password</label>
            <div class="mt-1">
              <input
                v-model="password"
                name="password"
                type="password"
                autocomplete="off"
                required
                placeholder="password"
                class="appearance-none block w-full px-3 py-2 rounded-md border border-gray-300 placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
              />
              <PasswordMeter
                style="margin-top: -5px"
                :password="password"
                :min-length="passwordMinLength"
                @score="(s) => (passwordValid = s == 4)"
              />
              <p class="text-gray-400">
                You must use a strong password: at least {{ passwordMinLength }} characters, with an upper case letter,
                a lower case letter, and a number.
              </p>
            </div>
          </div>

          <div>
            <button
              type="submit"
              class="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-600 disabled:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-600"
              :disabled="!email || !passwordValid || result === 'loading' || result === 'LocalSignUpResult.Success'"
              @click="onLocalSignUpStart()"
            >
              <div v-if="result === 'loading'">Signing up ...</div>
              <div v-else-if="result === 'LocalSignUpResult.Success'">Signed up</div>
              <div v-else>Sign up</div>
            </button>
          </div>
        </div>
        <div class="mt-6 flex w-full justify-center text-sm">
          <template v-if="result === 'loading'">
            <ScalingDots />
          </template>
          <template v-else-if="result === 'exception'">
            <XCircleIcon class="w-5 h-5 text-red-600" aria-hidden="true" />
            <span class="ml-2 text-red-600">failed to contact the server, please retry</span>
          </template>
          <template v-else-if="result === 'LocalSignUpResult.Fail'">
            <XCircleIcon class="w-5 h-5 text-red-600" aria-hidden="true" />
            <span class="ml-2 text-red-600">internal error occurred, please retry</span>
          </template>
          <template v-else-if="result === 'LocalSignUpResult.Success'">
            <CheckCircleIcon class="w-5 h-5 text-green-600" aria-hidden="true" />
            <span class="ml-2 text-green-600">success</span>
          </template>
          <template v-else>
            <!-- dummy - keep height -->
            <span>&nbsp;</span>
          </template>
        </div>

        <div class="mt-6">
          <div class="relative">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-gray-300" />
            </div>
            <div class="relative flex justify-center text-sm">
              <span class="px-2 bg-white text-gray-500"> Or sign up with </span>
            </div>
          </div>

          <div class="mt-6 grid grid-cols-2 gap-2">
            <div>
              <a
                :href="SignUpGoogleUrl"
                class="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
              >
                <span class="sr-only">Sign in with Google</span>
                <GoogleIcon class="w-5 h-5" aria-hidden="true" />
              </a>
            </div>

            <div>
              <a
                :href="SignUpGithubUrl"
                class="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
              >
                <span class="sr-only">Sign in with GitHub</span>
                <svg class="w-5 h-5" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fill-rule="evenodd"
                    d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z"
                    clip-rule="evenodd"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md text-center text-gray-500">
      Already have an account?
      <a :href="signInRoute" class="font-medium underline text-primary hover:text-primary-600">Sign In</a>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  AppName,
  ScreenRoute,
  Strategy,
  Url,
  LocalSignUpRes,
  LocalSignUpResult,
  OauthSignUpResult,
  OauthSignUpResult_Success,
  OauthSignUpResult_UserAlreadyRegistered,
  ScreenName,
  Flow,
} from "@spike/app2-sitemap/src/auth.js";
import axios from "axios";
import ModalException from "../components/SignUp/ModalException.vue";
import ModalCantRetrieveEmail from "../components/SignUp/ModalCantRetrieveEmail.vue";
import ModalUserAlreadyRegistered from "../components/SignUp/ModalUserAlreadyRegistered.vue";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/vue/solid";
import GoogleIcon from "@icons/brand/Google";
import sleep from "@util/sleep";
import { onMounted, ref } from "vue";
import PasswordMeter from "../components/password-meter.vue";
import ScalingDots from "../components/spinners/ScalingDots.vue";
import * as Nav from "../navigation";
import { Screen } from "../routes";
import * as Store from "../store";

const signInRoute = ref("#" + ScreenRoute.SignIn);
const email = ref<string | null>(null);
const password = ref(null);
const passwordMinLength = ref<number>(6);
const passwordValid = ref<boolean>(false);
const SignUpGithubUrl = ref(Url.server.github.signUp);
const SignUpGoogleUrl = ref(Url.server.google.signUp);
const returnUrl = query("returnUrl") as string | null;
if (returnUrl) {
  SignUpGithubUrl.value = SignUpGithubUrl.value + "?returnUrl=" + returnUrl; // add returnUrl if present
  SignUpGoogleUrl.value = SignUpGoogleUrl.value + "?returnUrl=" + returnUrl; // add returnUrl if present
}
const UserAlreadyRegisteredEmail = ref<string | null>(null);
const OauthProvider = ref<string | null>(null);

const result = ref<string | null>(null);
// Testing: local ajax results:
// result.value = "loading";
// result.value = "exception";
// result.value = "LocalSignUpResult.Success";
// result.value = "LocalSignUpResult.Fail";
// result.value = "LocalSignUpResult.UserAlreadyRegistered";
// email.value = "ilan@spikedata.co.za";

onMounted(async () => {
  try {
    // console.log("onMounted:", ScreenRoute.SignUp);

    // Testing: local hardcoded result:
    // if (result.value) {
    //   return processLocalSignUpResponse({ result: result.value });
    // }

    // oauth flows occur by redirect
    // local flows occur by ajax response
    // see /spike/v9/priv/stack/app.spikedata.co.za/src/sitemap/src/auth.ts : exports.serverRedirect.{github,google}.signUp.*

    // note: 2022-03-08 changed from querystring to redirectData:
    // - querystring: http://app.local.spikedata.co.za:3108/auth/?fail=-3&provider=google&email=ilancopelyn%40gmail.com#/sign-up/
    // - redirectData: http://app.local.spikedata.co.za:3108/auth/#/sign-up/user-already-registered/eyAicHJvdmlkZXIiOiAiZ2l0aHViIiwgImVtYWlsIjogImlsYW5Ac3Bpa2VkYXRhLmNvLnphIiB9
    //  - where: eyAicHJvdmlkZXIiOiAiZ2l0aHViIiwgImVtYWlsIjogImlsYW5Ac3Bpa2VkYXRhLmNvLnphIiB9 = { provider: "google", email: "ilancopelyn@gmail.com" }
    const redirectData = Nav.getRedirectData(AppName, ScreenRoute.SignUp);
    // Testing: oauth redirects:
    // const redirectData = { step: OauthSignUpResult.Success };
    // const redirectData = { step: OauthSignUpResult.Exception };
    // const redirectData = { step: OauthSignUpResult.CantRetrieveEmail };
    // const redirectData = { step: OauthSignUpResult.UserAlreadyRegistered, data: { provider: "github", email: "ilan@spikedata.co.za" } };
    if (redirectData && redirectData.step) {
      switch (redirectData.step) {
        case OauthSignUpResult.Success:
          return onOauthSignUpResultSuccess();
        case OauthSignUpResult.Exception:
          return onOauthSignUpResultException();
        case OauthSignUpResult.CantRetrieveEmail:
          return onOauthSignUpResultCantRetrieveEmail();
        case OauthSignUpResult.UserAlreadyRegistered:
          return onOauthSignUpResultUserAlreadyRegistered(redirectData.data as OauthSignUpResult_UserAlreadyRegistered);
        default:
        // console.error("broken code: unknown OauthSignUpResult:", redirectData.step);
      }
    }
  } catch (e) {
    // console.error("auth onMounted failed:", e);
  }
});

function query(param: string, isNumber = false) {
  const searchParams = new URLSearchParams(window.location.search);
  let p = searchParams.get(param);
  if (p) {
    return isNumber ? +p : p;
  }
  return null;
}

//#region Local sign-up

async function onLocalSignUpStart() {
  try {
    const _email = email.value;
    const _password = password.value;

    result.value = "loading";
    const response = await axios.post(Url.server.local.signUp, {
      // see { usernameField, passwordField } in auth.js
      email: _email,
      password: _password,
    });
    // console.log("response:", response);
    const res: LocalSignUpRes = response.data;
    processLocalSignUpResponse(res);
  } catch (e) {
    // console.error("sign-up failed:", e);
    onLocalSignUpException();
  }
}

function processLocalSignUpResponse(res: LocalSignUpRes) {
  // console.log("processLocalSignUpResponse");
  switch (res.result) {
    case LocalSignUpResult.Fail:
      onLocalSignUpFail();
      break;
    case LocalSignUpResult.Success:
      onLocalSignUpSuccess();
      break;
    case LocalSignUpResult.UserAlreadyRegistered:
      onLocalSignUpUserAlreadyRegistered();
      break;
    default:
      onLocalSignUpException();
      break;
  }
}

function onLocalSignUpException() {
  result.value = "exception";
}

function onLocalSignUpFail() {
  result.value = "LocalSignUpResult.Fail";
}

function onLocalSignUpUserAlreadyRegistered() {
  UserAlreadyRegisteredEmail.value = email.value;
  result.value = "LocalSignUpResult.UserAlreadyRegistered";
}

async function onLocalSignUpSuccess() {
  // show message
  result.value = "LocalSignUpResult.Success";

  // wait 2 seconds then go to
  await sleep(2000);

  Store.setEmail(email.value as string);
  Store.setStrategy(Strategy.local);
  Nav.startFlow(Flow.signUp_local);
  Nav.complete(ScreenName.SignUp);
  Nav.setActive(Screen.VerifyLocalEmail, true);
}

//#endregion

//#region Oauth sign-up

function onOauthSignUpResultSuccess() {
  // no need to show a modal like onLocalSignUpSuccess() - just go straight to team create (coming from a redirect)
  const redirectData = Nav.getRedirectData(AppName, ScreenRoute.SignUp);
  // console.log("redirectData:", ScreenRoute.SignUp, redirectData);
  if (redirectData) {
    let d = redirectData.data as OauthSignUpResult_Success;
    Store.setEmail(d.email);
    Store.setStrategy(d.strategy);
  }

  Nav.startFlow(Flow.signUp_oauth);
  Nav.complete(ScreenName.SignUp);
  Nav.setActive(Screen.TeamCreate, true);
}

function onOauthSignUpResultException() {
  result.value = "OauthSignUpResult.Exception";
}

function onOauthSignUpResultCantRetrieveEmail() {
  result.value = "OauthSignUpResult.CantRetrieveEmail";
}

function onOauthSignUpResultUserAlreadyRegistered(data: OauthSignUpResult_UserAlreadyRegistered) {
  OauthProvider.value = data.provider;
  UserAlreadyRegisteredEmail.value = data.email;
  result.value = "OauthSignUpResult.UserAlreadyRegistered";
}

function onModalCloseException(i: number) {
  result.value = null;
  Nav.setActive(Screen.SignUp, true); // already on SignUp screen, but will clear out step i.e. #/sign-up/exception => #/sign-up
}

function onModalCloseCantRetrieveEmail(i: number) {
  result.value = null;
  Nav.setActive(Screen.SignUp, true); // already on SignUp screen, but will clear out step i.e. #/sign-up/cant-retrieve-email => #/sign-up
}

function onModalCloseUserAlreadyRegistered(i: number) {
  UserAlreadyRegisteredEmail.value = null;
  if (i === 1) {
    // Go to sign-in
    Nav.setActive(Screen.SignIn, true);
  } else {
    // Retry
    result.value = null;
    Nav.setActive(Screen.SignUp, true); // already on SignUp screen, but will clear out step i.e. #/sign-up/user-already-registered/eyAicHJvdmlkZXIiOiAiZ2l0aHViIiwgImVtYWlsIjogImlsYW5Ac3Bpa2VkYXRhLmNvLnphIiB9 => #/sign-up
  }
}

//#endregion
</script>
